// Angular imports
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// Service imports
import { ScreenService } from '../../_services/shared-services/screen.service';
import { HelperService } from '../../_services/shared-services/helper.service';
// Constant imports
import { Constants } from '../../_constants/constants';
import { ImageConstants } from '../../_constants/image-constants';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-common-user-authentication-layout',
  templateUrl: './common-user-authentication-layout.component.html',
  styleUrls: ['./common-user-authentication-layout.component.scss'],
  standalone: false
})
export class CommonUserAuthenticationLayoutComponent
  implements OnInit, OnDestroy
{
  logoImage: string;
  helpImage: string;
  isSmallScreen: boolean = false;
  isQuickPayExpanded: boolean = false;
  isAccountInfoShow: boolean = false;
  loginHeaderText: string = 'Welcome Home!';
  privacyPolicyUrl: string = Constants.privacyPolicyUrl;
  aboutCiraNetUrl: string = Constants.aboutCiraNetUrl;
  termsAndConditionsUrl: string = Constants.termsAndConditionsUrl;
  isRMLogoHidden: boolean = false;
  showInactivityLogoutMessage: boolean = false;

  private initialViewportHeight: number;
  private resizeHandler: () => void;

  constructor(
    private screenService: ScreenService,
    private helperService: HelperService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.logoImage = ImageConstants.ciranetLogoImage;
    this.setInitialViewportHeight();
    this.updateScreenSize();
    this.checkInactivityLogout();
    this.checkQuickPayAutoExpand();
    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );
    RxjsUtils.safeSubscribe(
      this.helperService.subjectLoginHeaderText,
      (text: string) => {
        this.loginHeaderText = text;
      }
    );
    RxjsUtils.safeSubscribe(
      this.helperService.subjectIsRMLogoHidden,
      (isHidden: boolean) => {
        this.isRMLogoHidden = isHidden;
      }
    );
  }

  toggleQuickPay(event: Event): void {
    event.preventDefault();
    this.isQuickPayExpanded = !this.isQuickPayExpanded;
  }

  showAccountInfo(event: Event): void {
    event.preventDefault();
    this.isAccountInfoShow = true;
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
  }

  private setInitialViewportHeight(): void {
    // Capture initial viewport height before keyboard opens
    this.initialViewportHeight = window.innerHeight;
    document.documentElement.style.setProperty(
      '--app-height',
      `${this.initialViewportHeight}px`
    );

    // Only update on orientation change, not on keyboard open/close
    this.resizeHandler = () => {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;

      // Detect orientation change (width/height swap significantly)
      // Keyboard doesn't change width, only height
      if (Math.abs(currentWidth - window.screen.width) < 100) {
        // This is likely an orientation change, update the height
        if (currentHeight > this.initialViewportHeight) {
          this.initialViewportHeight = currentHeight;
          document.documentElement.style.setProperty(
            '--app-height',
            `${this.initialViewportHeight}px`
          );
        }
      }
    };

    window.addEventListener('resize', this.resizeHandler);
  }

  ngOnDestroy(): void {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  dismissInactivityMessage(): void {
    this.showInactivityLogoutMessage = false;
    sessionStorage.removeItem(StorageKeyConstants.inactivityLogoutKey);
  }

  private checkInactivityLogout(): void {
    const wasInactivityLogout = sessionStorage.getItem(
      StorageKeyConstants.inactivityLogoutKey
    );
    if (wasInactivityLogout === 'true') {
      this.showInactivityLogoutMessage = true;
    }
  }

  /**
   * Auto-expand Quick Pay section if there's a quickPayError or quickPaySuccess query param.
   * This ensures users see the Quick Pay card without having to click "Make quick payment".
   * - quickPayError: Shows error message for failed OAuth or no properties
   * - quickPaySuccess: Shows property selection after successful Google OAuth
   *
   * After expanding, clears the query params from URL to prevent issues with
   * bookmarking or page refresh showing stale state.
   */
  private checkQuickPayAutoExpand(): void {
    const quickPayError = this.route.snapshot.queryParams['quickPayError'];
    const quickPaySuccess = this.route.snapshot.queryParams['quickPaySuccess'];
    if (quickPayError || quickPaySuccess) {
      this.isQuickPayExpanded = true;
      // Clean up query params from URL without triggering navigation
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true
      });
    }
  }
}
